// App-state lifecycle wiring. Runs once at app boot.
//
// On background: kick the upload-queue drain handler best-effort. The native
// UploadWorker BGTask is also registered (AppDelegate), so even if this JS
// call doesn't finish before iOS suspends us, the drain still runs later.
//
// On foreground: drain the native LocationBG queue (v4.9). Points captured
// while the WebView was suspended/coalesced now flow into the JS outbox.
//
// IMPORTANT: GPS is intentionally NOT touched here. The LocationBG Swift
// plugin owns CoreLocation directly with allowsBackgroundLocationUpdates=true,
// so backgrounding keeps tracking running on the native side. See
// active-watch.ts for why we don't tear down the WatchHandle on unmount.

import { isNative } from "./platform";
import { triggerUploadDrain } from "./upload-drain";
import { drainNativeLocationQueue } from "./location-bg";
import { getCachedTripSync } from "@/lib/offline/trip-cache";
import { drainOnce as drainLiveActivityCommandsOnce } from "@/lib/live-activity/commands";


let installed = false;

export function initAppLifecycle(): void {
  if (installed) return;
  installed = true;
  if (!isNative()) return;

  void import("@capacitor/app")
    .then(({ App }) => {
      App.addListener("appStateChange", ({ isActive }) => {
        if (!isActive) {
          // Best-effort drain. If gates fail (no Wi-Fi, low battery), the
          // upload-drain handler is responsible for bailing cleanly — we
          // don't second-guess it here.
          triggerUploadDrain();
        } else {
          // Foreground — pull any points the native queue accumulated while
          // we were suspended.
          void drainNativeLocationQueue(getCachedTripSync()?.id ?? null);
          // Drain any Dynamic Island AppIntent taps queued while suspended.
          void drainLiveActivityCommandsOnce();
        }
      }).catch(() => {});
    })
    .catch(() => {});
}
